import { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import 'tippy.js/dist/tippy.css';

declare global {
	interface Window {
		formatCurrency: (number: string) => string;
		formatDecimal: (number: string) => string;
		clipText: (text: string) => void;
	}
	interface ObjectConstructor {
		typedKeys<T>(obj: T): Array<keyof T>;
	}
}
Object.typedKeys = Object.keys;

type Components =
	| 'Login'
	| 'Eftpos'
	| 'SignupToAES'
	| 'BankAccountPrompt'
	| 'ViewSurvey'
	| 'Utils'
	| 'Photos'
	| 'FirstTimePassword'
	| 'Maintenance'
	| 'ChangePassword'
	| 'AccountDetails'
	| 'InsuranceLicense'
	| 'NotFound'
	| 'EntertainerApplication'
	| 'EntertainerBookingConfirmation';

// Function to dynamically import components
const loadComponent = (componentName: Components) => {
	switch (componentName) {
		case 'Login':
			return lazy(() => import('./Components/Login/Login'));
		case 'Eftpos':
			return lazy(() => import('./Components/Eftpos/Eftpos'));
		case 'SignupToAES':
			return lazy(() => import('./Components/Prompts/SignupToAES'));
		case 'BankAccountPrompt':
			return lazy(() => import('./Components/BankAccountPrompt/BankAccountPrompt'));
		case 'ViewSurvey':
			return lazy(() => import('./Components/ViewSurvey/ViewSurvey'));
		case 'Utils':
			return lazy(() => import('./Components/Utils/Utils'));
		case 'Photos':
			return lazy(() => import('./Components/Photos/Photos'));
		case 'FirstTimePassword':
			return lazy(() => import('./Components/Login/FirstTimePassword'));
		case 'Maintenance':
			return lazy(() => import('./Components/Maintenance/Maintenance'));
		case 'ChangePassword':
			return lazy(() => import('./Components/Login/ChangePassword'));
		case 'AccountDetails':
			return lazy(() => import('./Components/AccountDetails/AccountDetails'));
		case 'InsuranceLicense':
			return lazy(() => import('./Components/InsuranceLicense/InsuranceLicense'));
		case 'NotFound':
			return lazy(() => import('./Components/NotFound/NotFound'));
		case 'EntertainerApplication':
			return lazy(() => import('./Components/EntertainerApplication/EntertainerApplication'));
		case 'EntertainerBookingConfirmation':
			return lazy(() => import('./Components/EntertainerApplication/EntertainerBookingConfirmation'));
	}
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderComponent = (containerClass: string, componentName: Components, props?: any) => {
	const container = document.querySelector(containerClass);
	if (!container) {
		throw new Error(`Container with class ${containerClass} not found`);
	}
	const Component = loadComponent(componentName);
	const root = ReactDOM.createRoot(container);
	// Use React's Suspense to handle loading state while the component is fetched
	root.render(
		<Suspense fallback={null}>
			<Component {...props} />
		</Suspense>,
	);
};
